import { buildWorkspaceChatListParams } from "@/lib/workspaceChat";
import { workspaceUserDisplayName } from "@/modules/despacho/despachoWorkspaceAuxiliaries";

/** Tiempo relativo corto para el feed de actividad (es-MX). */
export function formatActivityRelativeTime(iso) {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 60_000) return "Ahora";
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 60) return `Hace ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `Hace ${diffH} h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return "Ayer";
  return date.toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
}

function snippetText(body, maxLen = 72) {
  const s = String(body || "").trim().replace(/\s+/g, " ");
  if (!s) return "";
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen - 1)}…`;
}

export function buildActivityFromChatMessage(msg) {
  const name = String(msg?.sender_name || "").trim() || "Usuario";
  const body = String(msg?.body || "").trim();
  const attachments = Array.isArray(msg?.attachments) ? msg.attachments : [];
  let texto;
  if (body) {
    const quote = snippetText(body);
    texto = quote
      ? `${name} escribió en el chat interno: «${quote}»`
      : `${name} escribió en el chat interno`;
  } else if (attachments.length) {
    texto = `${name} compartió ${attachments.length} archivo(s) en el chat interno`;
  } else {
    texto = `${name} envió un mensaje en el chat interno`;
  }
  return {
    id: `chat-${msg.id}`,
    tipo: "mensaje",
    texto,
    hora: formatActivityRelativeTime(msg.created_at),
    sortAt: msg.created_at || "",
  };
}

export function buildActivityFromPresenceUser(user, { online = true } = {}) {
  const name = workspaceUserDisplayName(user);
  return {
    id: online ? `presence-on-${user.id}` : `presence-off-${user.id}`,
    tipo: online ? "conectado" : "ausente",
    texto: online ? `${name} está conectado/a` : `${name} está ausente`,
    hora: online ? "Ahora" : formatActivityRelativeTime(user.last_seen_at),
    sortAt: online ? new Date().toISOString() : user.last_seen_at || "",
  };
}

/**
 * Feed operativo: presencia en línea + mensajes recientes del chat interno.
 */
export function buildDespachoTeamActivityFeed({
  messages = [],
  presenceItems = [],
  maxItems = 15,
  excludeUserId = "",
} = {}) {
  const rows = [];
  const online = presenceItems.filter((p) => p.is_online && p.id !== excludeUserId);
  const offline = presenceItems.filter(
    (p) => !p.is_online && p.id !== excludeUserId && !p.is_buyer_tenant,
  );

  online.slice(0, 4).forEach((u) => rows.push(buildActivityFromPresenceUser(u, { online: true })));

  const sortedMsgs = [...messages].sort((a, b) =>
    String(b.created_at || "").localeCompare(String(a.created_at || "")),
  );
  sortedMsgs.slice(0, 12).forEach((m) => rows.push(buildActivityFromChatMessage(m)));

  if (rows.length < maxItems && offline.length) {
    const away = [...offline].sort((a, b) =>
      String(a.last_seen_at || "").localeCompare(String(b.last_seen_at || "")),
    )[0];
    if (away) rows.push(buildActivityFromPresenceUser(away, { online: false }));
  }

  return rows.slice(0, maxItems);
}

export function buildDespachoTeamActivityFetchParams() {
  return buildWorkspaceChatListParams({ limit: 20 });
}
